<!--
  Built on the shared dialog kit (.sd-scrim / .sd-modal / .sd-form in shared/styles/_dialog.scss),
  laid out as the Code transaction dialog (manual-coding-modal) is, so coding an expense by hand
  looks the same wherever it starts. What differs is only what a hand-entered expense needs on
  top: a date, an amount and where it was paid from.
-->
<div class="sd-scrim" (click)="close()">
  <div class="sd-modal sd-modal--form" role="dialog" aria-modal="true" aria-labelledby="em-title"
    (click)="$event.stopPropagation()">
    <div class="sd-form">

      <div class="sd-form__head">
        <div class="sd-form__head-text">
          <h3 class="sd-form__title" id="em-title">
            <ng-container *ngIf="!isBulkMode">Code expense manually</ng-container>
            <ng-container *ngIf="isBulkMode">
              Code {{ bulkTransactionIds.length }} transaction{{ bulkTransactionIds.length === 1 ? '' : 's' }}
            </ng-container>
          </h3>
          <p class="sd-form__subtitle">
            <ng-container *ngIf="!isBulkMode">No document? Enter the details and it goes to your Inbox like any other expense.</ng-container>
            <ng-container *ngIf="isBulkMode">One set of details, applied to all {{ bulkTransactionIds.length }}.</ng-container>
          </p>
        </div>
        <button type="button" class="sd-icon-btn" aria-label="Close" (click)="close($event)">
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <form class="sd-form__body" [formGroup]="expenseForm" role="form" name="expenseForm" (ngSubmit)="onSubmit()"
        novalidate>

        <div class="sd-field">
          <label class="sd-field__label" for="ddlsupplier">Supplier</label>
          <ng-select [ngModel]="selectedSupplier || null" [items]="suppliers" formControlName="supplier"
            [searchable]="true" [clearable]="false" bindLabel="name" (change)="onSelectSupplier($event)"
            (keyup)="handleSupplierName($event.target.value)" placeholder="Select a supplier" [minlength]="0"
            class="sd-picker" appendTo="body" [ngModelOptions]="{standalone: true}" (blur)="handleSupplierBlur()"
            [closeOnSelect]="true" id="ddlsupplier">
            <ng-template ng-option-tmp let-item="item" let-index="index">
              <div class="tax-item" [innerHTML]="item.name"></div>
            </ng-template>
            <ng-template ng-footer-tmp
              *ngIf="isCreateSupplierItemShown || (isNewSupplier && isCreateSupplierItemShown)">
              <div class="create-new-supplier" (click)="createNewSupplier()">
                <span class="spinner-wrapper" *ngIf="creatingNewSupplier" style="margin-right: 5px;">
                  <mat-spinner [diameter]="18"></mat-spinner>
                </span>
                <span *ngIf="!creatingNewSupplier">+ Create '{{newSupplier.name}}'</span>
                <span *ngIf="creatingNewSupplier" style="padding-top: 2px;">Adding '{{newSupplier.name}}'</span>
              </div>
            </ng-template>
          </ng-select>
        </div>

        <div class="sd-field">
          <label class="sd-field__label" for="em-account">Account</label>
          <ng-select id="em-account" [ngModel]="account?.name || null" [items]="accounts" bindLabel="name"
            [searchable]="true" [clearable]="false" (change)="onSelectAccount($event)" placeholder="Select an account"
            [minlength]="0" class="sd-picker" appendTo="body" [ngModelOptions]="{ standalone: true }" name="account"
            formControlName="account">
            <ng-template ng-option-tmp let-item="item">
              <div *ngIf="item.isActive === true">{{ item.name }}</div>
            </ng-template>
          </ng-select>
        </div>

        <div class="sd-form__row" *ngIf="!isBulkMode">
          <div class="sd-field">
            <label class="sd-field__label" for="em-date">Date</label>
            <div class="em-date">
              <input id="em-date" class="sd-input" [matDatepicker]="picker" formControlName="date"
                [disabled]="!isDateEditable" [placeholder]="dateFormat === 'en-US' ? 'MM/DD/YY' : 'DD/MM/YY'"
                [class.is-invalid]="expenseForm.get('date').invalid && expenseForm.get('date').touched"
                (click)="isDateEditable && picker.open()" />
              <mat-datepicker-toggle class="em-date__toggle" [for]="picker"></mat-datepicker-toggle>
              <mat-datepicker #picker></mat-datepicker>
            </div>
          </div>

          <div class="sd-field">
            <label class="sd-field__label" for="em-amount">Amount</label>
            <input type="number" id="em-amount" formControlName="amount" class="sd-input em-amount" placeholder="0.00"
              step="0.01" min="0" pattern="^\d*\.?\d{0,2}$" (input)="onAmountInput($event)"
              [disabled]="!isAmountEditable"
              [class.is-invalid]="expenseForm.get('amount').invalid && expenseForm.get('amount').touched" />
          </div>
        </div>

        <!-- Two columns only when Class shares the row; alone, Tax is as wide as the rest. -->
        <div [class.sd-form__row]="IsQuickBook && qboClasses.length > 0">
          <div class="sd-field">
            <label class="sd-field__label" for="em-tax">Tax</label>
            <ng-select id="em-tax" placeholder="Select a tax rate" [searchable]="false" [items]="taxRates"
              [clearable]="false" bindLabel="name" bindValue="id" formControlName="selectedTaxRate"
              class="sd-picker" appendTo="body" (change)="onSelectTaxRates($event)"
              [ngModelOptions]="{standalone: true}" [ngModel]="organizationTaxRateId || null">
            </ng-select>
          </div>

          <div class="sd-field" *ngIf="IsQuickBook && qboClasses.length > 0">
            <label class="sd-field__label" for="em-class">
              Class <span class="sd-field__optional">Optional</span>
            </label>
            <ng-select id="em-class" [items]="qboClasses" bindLabel="name" bindValue="id" [searchable]="true"
              [clearable]="true" placeholder="Select a class" class="sd-picker" appendTo="body"
              [(ngModel)]="selectedQBOClassId" [ngModelOptions]="{standalone: true}">
            </ng-select>
          </div>
        </div>

        <div class="sd-field" *ngIf="!isPaperwork && !isBulkMode">
          <label class="sd-field__label" for="em-paid-from">Paid from</label>
          <ng-select id="em-paid-from" [ngModel]="paymentAccountNumber || null" [items]="bankAccountList"
            [searchable]="true" formControlName="paymentAccountNumber" [clearable]="false" bindLabel="name"
            bindValue="accountID" placeholder="Select a payment account" class="sd-picker" appendTo="body"
            (change)="onSelectPaymentAccount($event)" [ngModelOptions]="{standalone: true}">
          </ng-select>
        </div>

        <div class="sd-field">
          <label class="sd-field__label" for="em-details">Details</label>
          <textarea id="em-details" formControlName="details" class="sd-input em-details" rows="3"
            placeholder="What was this for?"
            [class.is-invalid]="expenseForm.get('details').invalid && expenseForm.get('details').touched"></textarea>
        </div>

        <!-- Lets Enter submit from any field. -->
        <button type="submit" hidden></button>
      </form>

      <div class="sd-form__footer">
        <button type="button" class="sd-btn sd-btn--secondary" (click)="close($event)">Cancel</button>
        <button type="button" class="sd-btn sd-btn--primary" (click)="onSubmit()"
          [disabled]="!expenseForm.valid || isFileUploadProcessing">
          <ng-container *ngIf="isFileUploadProcessing">Adding…</ng-container>
          <ng-container *ngIf="!isFileUploadProcessing && !isBulkMode">Add expense</ng-container>
          <ng-container *ngIf="!isFileUploadProcessing && isBulkMode">
            Code {{ bulkTransactionIds.length }} transaction{{ bulkTransactionIds.length === 1 ? '' : 's' }}
          </ng-container>
        </button>
      </div>
    </div>
  </div>
</div>
